/**
 * BOT Hero Banner - Carousel Controller
 * Handles autoplay, arrows, dots, fade/slide transitions, pause on hover, loop
 * 100% autonomo, sin dependencias del tema
 */

(function () {
  'use strict';

  class BotHeroCarousel {
    constructor(section) {
      this.section = section;
      this.carousel = section.querySelector('[data-hero-carousel]');
      if (!this.carousel) return;

      this.slides = Array.from(this.carousel.querySelectorAll('.bot-hero__slide'));
      if (this.slides.length <= 1) return;

      this.currentIndex = 0;
      this.isAnimating = false;
      this.autoplayTimer = null;
      this.isPaused = false;

      /* Read data attributes */
      this.autoplay = this.carousel.dataset.autoplay === 'true';
      this.speed = (parseInt(this.carousel.dataset.speed, 10) || 5) * 1000;
      this.transition = this.carousel.dataset.transition || 'fade';
      this.pauseOnHover = this.carousel.dataset.pause === 'true';
      this.loop = this.carousel.dataset.loop === 'true';

      /* Navigation elements */
      this.prevBtn = section.querySelector('[data-hero-prev]');
      this.nextBtn = section.querySelector('[data-hero-next]');
      this.dots = Array.from(section.querySelectorAll('[data-hero-dot]'));

      this.init();
    }

    init() {
      this.bindArrows();
      this.bindDots();
      this.bindHover();
      this.bindKeyboard();
      this.bindTouch();

      if (this.autoplay) {
        this.startAutoplay();
      }
    }

    /* ------------------------------------------------------------------ */
    /*  Navigation
    /* ------------------------------------------------------------------ */

    goTo(index, direction) {
      if (this.isAnimating) return;
      if (index === this.currentIndex) return;

      const total = this.slides.length;

      /* Handle loop / bounds */
      if (index < 0) {
        if (this.loop) {
          index = total - 1;
        } else {
          return;
        }
      }
      if (index >= total) {
        if (this.loop) {
          index = 0;
        } else {
          return;
        }
      }

      /* Determine direction if not explicitly passed */
      if (typeof direction === 'undefined') {
        direction = index > this.currentIndex ? 'next' : 'prev';
      }

      this.isAnimating = true;

      const currentSlide = this.slides[this.currentIndex];
      const nextSlide = this.slides[index];

      if (this.transition === 'slide') {
        this.transitionSlide(currentSlide, nextSlide, direction);
      } else {
        this.transitionFade(currentSlide, nextSlide);
      }

      this.currentIndex = index;
      this.updateDots();
    }

    next() {
      this.goTo(this.currentIndex + 1, 'next');
    }

    prev() {
      this.goTo(this.currentIndex - 1, 'prev');
    }

    /* ------------------------------------------------------------------ */
    /*  Transitions
    /* ------------------------------------------------------------------ */

    transitionFade(currentSlide, nextSlide) {
      /* Fade: simply toggle the active class, CSS handles opacity transition */
      nextSlide.classList.add('bot-hero__slide--active');
      currentSlide.classList.remove('bot-hero__slide--active');

      /* Wait for CSS transition to finish */
      this.waitTransitionEnd(nextSlide, () => {
        this.isAnimating = false;
      });
    }

    transitionSlide(currentSlide, nextSlide, direction) {
      const isNext = direction === 'next';

      /* Position the incoming slide off-screen */
      nextSlide.style.transition = 'none';
      nextSlide.classList.remove('bot-hero__slide--active');

      if (isNext) {
        nextSlide.classList.add('bot-hero__slide--enter-right');
      } else {
        nextSlide.classList.add('bot-hero__slide--enter-left');
      }

      /* Force reflow */
      void nextSlide.offsetHeight;

      /* Re-enable transitions */
      nextSlide.style.transition = '';

      /* Start the animation */
      requestAnimationFrame(() => {
        /* Remove enter positioning and add active */
        nextSlide.classList.remove('bot-hero__slide--enter-right', 'bot-hero__slide--enter-left');
        nextSlide.classList.add('bot-hero__slide--active');

        /* Move current slide out */
        currentSlide.classList.remove('bot-hero__slide--active');
        if (isNext) {
          currentSlide.classList.add('bot-hero__slide--exit-left');
        } else {
          currentSlide.classList.add('bot-hero__slide--exit-right');
        }

        this.waitTransitionEnd(nextSlide, () => {
          currentSlide.classList.remove(
            'bot-hero__slide--exit-left',
            'bot-hero__slide--exit-right'
          );
          this.isAnimating = false;
        });
      });
    }

    waitTransitionEnd(el, callback) {
      const handler = () => {
        el.removeEventListener('transitionend', handler);
        callback();
      };
      el.addEventListener('transitionend', handler);

      /* Fallback timeout in case transitionend doesn't fire */
      setTimeout(() => {
        el.removeEventListener('transitionend', handler);
        callback();
      }, 800);
    }

    /* ------------------------------------------------------------------ */
    /*  Autoplay
    /* ------------------------------------------------------------------ */

    startAutoplay() {
      this.stopAutoplay();
      this.autoplayTimer = setInterval(() => {
        if (!this.isPaused) {
          this.next();
        }
      }, this.speed);
    }

    stopAutoplay() {
      if (this.autoplayTimer) {
        clearInterval(this.autoplayTimer);
        this.autoplayTimer = null;
      }
    }

    resetAutoplay() {
      if (this.autoplay) {
        this.startAutoplay();
      }
    }

    /* ------------------------------------------------------------------ */
    /*  Dots
    /* ------------------------------------------------------------------ */

    updateDots() {
      this.dots.forEach((dot, i) => {
        dot.classList.toggle('bot-hero__dot--active', i === this.currentIndex);
      });
    }

    /* ------------------------------------------------------------------ */
    /*  Event Binding
    /* ------------------------------------------------------------------ */

    bindArrows() {
      if (this.prevBtn) {
        this.prevBtn.addEventListener('click', (e) => {
          e.preventDefault();
          this.prev();
          this.resetAutoplay();
        });
      }
      if (this.nextBtn) {
        this.nextBtn.addEventListener('click', (e) => {
          e.preventDefault();
          this.next();
          this.resetAutoplay();
        });
      }
    }

    bindDots() {
      this.dots.forEach((dot) => {
        dot.addEventListener('click', (e) => {
          e.preventDefault();
          const index = parseInt(dot.dataset.heroDot, 10);
          this.goTo(index);
          this.resetAutoplay();
        });
      });
    }

    bindHover() {
      if (!this.pauseOnHover) return;

      this.section.addEventListener('mouseenter', () => {
        this.isPaused = true;
      });
      this.section.addEventListener('mouseleave', () => {
        this.isPaused = false;
      });
    }

    bindKeyboard() {
      this.section.setAttribute('tabindex', '0');
      this.section.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') {
          e.preventDefault();
          this.prev();
          this.resetAutoplay();
        } else if (e.key === 'ArrowRight') {
          e.preventDefault();
          this.next();
          this.resetAutoplay();
        }
      });
    }

    bindTouch() {
      let startX = 0;
      let startY = 0;
      let distX = 0;
      let distY = 0;
      const threshold = 50;

      this.carousel.addEventListener('touchstart', (e) => {
        startX = e.touches[0].clientX;
        startY = e.touches[0].clientY;
      }, { passive: true });

      this.carousel.addEventListener('touchmove', (e) => {
        distX = e.touches[0].clientX - startX;
        distY = e.touches[0].clientY - startY;
      }, { passive: true });

      this.carousel.addEventListener('touchend', () => {
        /* Only trigger if horizontal swipe is dominant */
        if (Math.abs(distX) > threshold && Math.abs(distX) > Math.abs(distY)) {
          if (distX < 0) {
            this.next();
          } else {
            this.prev();
          }
          this.resetAutoplay();
        }
        distX = 0;
        distY = 0;
      }, { passive: true });
    }
  }

  /* ------------------------------------------------------------------ */
  /*  Initialization
  /* ------------------------------------------------------------------ */

  function initAllCarousels() {
    document.querySelectorAll('.bot-hero').forEach((section) => {
      if (!section._botHeroCarousel) {
        section._botHeroCarousel = new BotHeroCarousel(section);
      }
    });
  }

  /* Initialize on DOM ready */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAllCarousels);
  } else {
    initAllCarousels();
  }

  /* Re-initialize on Shopify section events (Theme Editor) */
  document.addEventListener('shopify:section:load', (e) => {
    const section = e.target.querySelector('.bot-hero');
    if (section) {
      section._botHeroCarousel = new BotHeroCarousel(section);
    }
  });

  document.addEventListener('shopify:section:unload', (e) => {
    const section = e.target.querySelector('.bot-hero');
    if (section && section._botHeroCarousel) {
      section._botHeroCarousel.stopAutoplay();
      delete section._botHeroCarousel;
    }
  });

  /* Handle block select in Theme Editor - go to that slide */
  document.addEventListener('shopify:block:select', (e) => {
    const slide = e.target.closest('.bot-hero__slide');
    if (!slide) return;
    const section = slide.closest('.bot-hero');
    if (!section || !section._botHeroCarousel) return;

    const index = parseInt(slide.dataset.slide, 10);
    const carousel = section._botHeroCarousel;
    carousel.stopAutoplay();
    carousel.goTo(index);
  });

  document.addEventListener('shopify:block:deselect', (e) => {
    const slide = e.target.closest('.bot-hero__slide');
    if (!slide) return;
    const section = slide.closest('.bot-hero');
    if (!section || !section._botHeroCarousel) return;
    section._botHeroCarousel.resetAutoplay();
  });
})();